import { ApolloError, gql, useMutation } from '@apollo/client';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import * as Yup from 'yup';

import { CloseModalFn } from 'src/components/common/URLModal';
import { RadioButton } from 'src/components/radioButton/RadioButton';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { SharedCheckConfigCardProps } from '../common/SharedCheckConfigCardProps';
import { VariantCheckConfigFragment } from '../VariantCheckConfigFragment';

const variantCheckConfigExcludedOperationsMutation = gql`
  mutation VariantCheckConfigEnableOperationsCheckMutation(
    $graphId: ID!
    $graphVariant: String!
    $enabled: Boolean!
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationEnableOperationsCheck(enabled: $enabled) {
          id
          ...VariantCheckConfigFragment
        }
      }
    }
  }
  ${VariantCheckConfigFragment}
`;

type Props = Omit<
  SharedCheckConfigCardProps,
  'graphCheckConfig' | 'variantCheckConfig'
> & {
  isOpen: boolean;
  onClose: CloseModalFn;
  setUpdateConfigError: Dispatch<SetStateAction<ApolloError | null>>;
  operationsChecksEnabled: boolean;
};

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  enabled: Yup.boolean().required(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

export const OperationChecksModal = ({
  isOpen,
  onClose,
  graphRef,
  operationsChecksEnabled,
  roles,
  setUpdateConfigError,
}: Props) => {
  const [updateCheckConfig] = useMutation<
    GraphQLTypes.VariantCheckConfigEnableOperationsCheckMutation,
    GraphQLTypes.VariantCheckConfigEnableOperationsCheckMutationVariables
  >(variantCheckConfigExcludedOperationsMutation, {
    onError: setUpdateConfigError,
  });

  const { values, isSubmitting, setFieldValue, submitForm } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: {
        enabled: operationsChecksEnabled,
      },
      onSubmit: async (formValues) => {
        await updateCheckConfig({
          variables: {
            graphId: graphRef.graphId,
            graphVariant: graphRef.graphVariant,
            enabled: formValues.enabled,
          },
        });
      },
    });

  const clearErrorsAndSubmit = useCallback(() => {
    setUpdateConfigError(null);
    submitForm();
  }, [setUpdateConfigError, submitForm]);

  const [toggleSetting, setToggleSetting] = useState<boolean>(
    operationsChecksEnabled,
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Run Operation Checks</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="grid gap-4">
            <RadioButton
              title={`On${operationsChecksEnabled ? ' (Active)' : ''}`}
              description="Recommended. This will check schema changes against historical operations and help prevent breaking changes."
              disabled={isSubmitting}
              selected={toggleSetting}
              onClick={() => {
                setToggleSetting(true);
              }}
            />
            <RadioButton
              title={`Off${operationsChecksEnabled ? '' : ' (Active)'}`}
              description="You should only consider turning off operation checks if your variant is used for active development."
              disabled={isSubmitting}
              selected={!toggleSetting}
              onClick={() => {
                setToggleSetting(false);
              }}
            />
          </div>
          <div className="mt-3 flex items-baseline">
            <IconWarning className="mr-2 text-icon-warning" />
            <span>
              Before disabling operation checks make sure that you are running
              rover <span className="font-bold">v0.12.0 or higher</span>.
            </span>
          </div>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button
              variant="secondary"
              isDisabled={!roles.canWriteCheckConfiguration || isSubmitting}
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting}
              isDisabled={
                toggleSetting === values.enabled ||
                !roles.canWriteCheckConfiguration
              }
              onClick={() => {
                setFieldValue('enabled', toggleSetting);
                clearErrorsAndSubmit();
                onClose();
              }}
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
